import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { useAuth } from "../context/ApiKeyContext";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import {
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight,
  Download,
  SkipForward,
  SkipBack,
  Maximize2,
  AlertCircle,
  Save,
} from "lucide-react";
import { CircularProgress, Typography } from "@material-ui/core";
import { getApiBaseUrl } from "../config";

// Configurar worker do PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// URL base da API
const API_BASE_URL = getApiBaseUrl();

/**
 * Componente otimizado de visualização de PDF
 * Carrega apenas uma página por vez para economizar memória
 *
 * @param {string} token - Token de acesso ao arquivo (obrigatório)
 * @param {number} initialPageNumber - Página inicial a exibir (opcional, padrão: 1)
 * @param {Array} objects - Lista de objetos para destacar na página (opcional)
 * @param {string} width - Largura do componente (padrão: '100%')
 * @param {string} height - Altura do componente (padrão: '600px')
 * @param {boolean} showToolbar - Mostrar barra de ferramentas (padrão: true)
 * @param {string} fileName - Nome do arquivo para download (padrão: 'document.pdf')
 * @param {string} serviceName - Nome do serviço para adicionar ao parâmetro Servicename (obrigatório para download)
 * @param {function} onSaveSelectionBox - Callback para salvar alterações do retângulo (opcional)
 *                                        Recebe o selectionBox como parâmetro: {x1, y1, x2, y2, page}
 *                                        Deve retornar uma Promise
 * @param {boolean} currentItemIsImage - Indica se o item atual é do tipo imagem (opcional)
 * @param {function} onRequestPageChange - Callback para solicitar mudança de página ao componente pai (opcional)
 *                                         Recebe a nova página como parâmetro: (newPage) => Promise<boolean>
 *                                         Deve retornar true se a mudança foi permitida, false caso contrário
 * @param {string} jobId - ID do job (necessário para salvar referência de imagem)
 * @param {string} documentId - ID do documento sendo revisado (necessário para salvar referência de imagem)
 * @param {string} itemId - ID do item atual (necessário para salvar referência de imagem)
 * @param {string} documentoOrigemId - ID do documento de origem selecionado (necessário para salvar referência de imagem)
 * @param {function} onImageReferenceUpdated - Callback chamado após atualização bem-sucedida da referência de imagem (opcional)
 *                                             Recebe os dados retornados pela API incluindo o novo token da imagem
 *
 * NOTA: O componente implementa download internamente usando /files/download.
 * Não é necessário passar callback onDownload.
 */
const OptimizedPDFViewer = forwardRef(
  (
    {
      token,
      initialPageNumber = 1,
      objects = [],
      width = "100%",
      height = "600px",
      showToolbar = true,
      fileName = "document.pdf",
      serviceName = "PROCESS_DIGITAL_PDF", // Nome do serviço padrão
      onSaveSelectionBox = null, // Callback para salvar alterações: (selectionBox) => Promise
      currentItemIsImage = false, // Indica se o item atual é do tipo imagem
      onRequestPageChange = null, // Callback para solicitar mudança de página: (newPage) => Promise<boolean>
      jobId = null, // ID do job
      documentId = null, // ID do documento sendo revisado
      itemId = null, // ID do item atual
      documentoOrigemId = null, // ID do documento de origem selecionado
      onImageReferenceUpdated = null, // Callback após atualização bem-sucedida
      editavel = true, // Define se o documento é editável (desabilita criação/edição de área de imagem)
    },
    ref,
  ) => {
    const { token: authToken } = useAuth();

    // Estados
    const [totalPages, setTotalPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(initialPageNumber);
    const [pageBase64, setPageBase64] = useState(null);
    const [scale, setScale] = useState(1.0);
    // null = não está sendo editado (input mostra currentPage); string = valor digitado pelo usuário,
    // inclusive '' (input vazio durante edição). Usar null como sentinela permite que o usuário
    // apague todos os caracteres sem que o valor "salte" de volta para currentPage.
    const [pageInput, setPageInput] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingPageCount, setLoadingPageCount] = useState(true);
    const [error, setError] = useState(null);
    const [containerDimensions, setContainerDimensions] = useState({
      width: 0,
      height: 0,
    });
    const [pageWidth, setPageWidth] = useState(0);
    const [pageHeight, setPageHeight] = useState(0);
    const [selectionBox, setSelectionBox] = useState(null); // { x1, y1, x2, y2, page }
    const [originalSelectionBox, setOriginalSelectionBox] = useState(null); // Estado inicial para comparação
    const [hasChanges, setHasChanges] = useState(false); // Indica se há mudanças pendentes
    const [saving, setSaving] = useState(false); // Indica se está salvando
    // NÃO usar useState para isDragging/isResizing - causam re-renders que removem os listeners!
    // const [isDragging, setIsDragging] = useState(false);
    // const [isResizing, setIsResizing] = useState(false);
    // const [resizeHandle, setResizeHandle] = useState(null); // 'tl', 'tr', 'bl', 'br'
    // const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const containerRef = useRef(null);
    const selectionBoxRef = useRef(null);
    const hasRequestedPageCount = useRef(false); // Evitar múltiplas chamadas ao page-count
    const isDraggingRef = useRef(false);
    const isResizingRef = useRef(false);
    const resizeHandleRef = useRef(null);
    const dragStartRef = useRef({ x: 0, y: 0 });
    const selectionBoxStateRef = useRef(null); // Ref para o estado atual do selectionBox
    const scaleRef = useRef(scale); // Ref para scale evitar recriar handleMouseMove
    const pageWidthRef = useRef(pageWidth); // Ref para pageWidth
    const pageHeightRef = useRef(pageHeight); // Ref para pageHeight

    // Manter refs sincronizadas com os estados
    useEffect(() => {
      selectionBoxStateRef.current = selectionBox;
    }, [selectionBox]);

    useEffect(() => {
      scaleRef.current = scale;
    }, [scale]);

    useEffect(() => {
      pageWidthRef.current = pageWidth;
    }, [pageWidth]);

    useEffect(() => {
      pageHeightRef.current = pageHeight;
    }, [pageHeight]);

    // Detectar mudanças no selectionBox
    useEffect(() => {
      console.log("[CHANGE DETECTION]", {
        selectionBox,
        originalSelectionBox,
      });

      // Se não há selectionBox, não há mudanças
      if (!selectionBox) {
        console.log("[CHANGE DETECTION] Sem selectionBox, sem mudanças");
        setHasChanges(false);
        return;
      }

      // Se há selectionBox mas não há original, significa que é um novo retângulo criado
      // Marcar como tendo mudanças
      if (!originalSelectionBox) {
        console.log(
          "[CHANGE DETECTION] Novo retângulo criado (sem original), marcando como tendo mudanças",
        );
        setHasChanges(true);
        return;
      }

      // Comparar coordenadas e página
      const hasPositionChange =
        Math.abs(selectionBox.x1 - originalSelectionBox.x1) > 0.1 ||
        Math.abs(selectionBox.y1 - originalSelectionBox.y1) > 0.1 ||
        Math.abs(selectionBox.x2 - originalSelectionBox.x2) > 0.1 ||
        Math.abs(selectionBox.y2 - originalSelectionBox.y2) > 0.1;

      const hasPageChange = selectionBox.page !== originalSelectionBox.page;

      console.log("[CHANGE DETECTION] Resultado:", {
        hasPositionChange,
        hasPageChange,
        willSetHasChanges: hasPositionChange || hasPageChange,
      });

      setHasChanges(hasPositionChange || hasPageChange);
    }, [selectionBox, originalSelectionBox]);

    // Expor métodos para componentes externos
    useImperativeHandle(ref, () => ({
      goToPage: (page) => {
        const targetPage = Math.max(1, Math.min(page, totalPages || 1));
        setCurrentPage(targetPage);
        setPageInput(null);
      },
      getCurrentPage: () => currentPage,
      getTotalPages: () => totalPages,
      setSelectionBox: (coordinates) => {
        // coordinates: { x1, y1, x2, y2, page }
        if (
          coordinates &&
          coordinates.x1 !== undefined &&
          coordinates.y1 !== undefined &&
          coordinates.x2 !== undefined &&
          coordinates.y2 !== undefined
        ) {
          const newBox = {
            x1: coordinates.x1,
            y1: coordinates.y1,
            x2: coordinates.x2,
            y2: coordinates.y2,
            page: coordinates.page || currentPage,
          };
          setSelectionBox(newBox);
          // Salvar como original (estado inicial)
          setOriginalSelectionBox(newBox);
          setHasChanges(false);
        } else {
          setSelectionBox(null);
          setOriginalSelectionBox(null);
          setHasChanges(false);
        }
      },
      clearSelectionBox: () => {
        setSelectionBox(null);
        setOriginalSelectionBox(null);
        setHasChanges(false);
      },
      getSelectionBox: () => selectionBox,
      getHasChanges: () => hasChanges,
    }));

    // Atualizar dimensões do container
    useEffect(() => {
      const updateDimensions = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          setContainerDimensions({ width: rect.width, height: rect.height });
        }
      };

      updateDimensions();
      window.addEventListener("resize", updateDimensions);
      return () => window.removeEventListener("resize", updateDimensions);
    }, []);

    // Função para buscar a quantidade total de páginas
    const fetchPageCount = async () => {
      try {
        setError(null);
        setLoadingPageCount(true);

        const url = `${API_BASE_URL}/files/page-count?token=${encodeURIComponent(token)}&Servicename=${encodeURIComponent(serviceName)}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(
            `Erro ao buscar total de páginas: ${response.status} - ${response.statusText}`,
          );
        }

        const data = await response.json();
        setTotalPages(data.total_paginas);
      } catch (err) {
        console.error("Erro ao buscar total de páginas:", err);
        setError(`Falha ao carregar informações do PDF: ${err.message}`);
      } finally {
        setLoadingPageCount(false);
      }
    };

    // Função para buscar uma página específica
    const fetchPage = async (pageNumber) => {
      try {
        setError(null);
        setLoading(true);
        setPageBase64(null);

        const url = `${API_BASE_URL}/files/preview?Servicename=${encodeURIComponent(serviceName)}`;

        const requestBody = {
          token: token,
          pagina: pageNumber,
          objetos: objects || [],
        };

        const response = await fetch(url, {
          method: "POST",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error(
            `Erro ao buscar página ${pageNumber}: ${response.status} - ${response.statusText}`,
          );
        }

        const data = await response.json();
        setPageBase64(data.pagina_base64);
      } catch (err) {
        console.error("Erro ao buscar página:", err);
        setError(`Falha ao carregar página ${pageNumber}: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    // Buscar quantidade total de páginas ao montar o componente
    useEffect(() => {
      if (
        token &&
        authToken &&
        totalPages === null &&
        !hasRequestedPageCount.current
      ) {
        hasRequestedPageCount.current = true;
        fetchPageCount();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, authToken]);

    // Quando totalPages chegar: sincronizar currentPage com initialPageNumber se necessário
    // e buscar a página (será a página sincronizada ou a atual)
    useEffect(() => {
      if (totalPages !== null && token && authToken) {
        // Se initialPageNumber é diferente de currentPage, atualizar primeiro
        if (initialPageNumber && initialPageNumber !== currentPage) {
          setCurrentPage(initialPageNumber);
          // fetchPage será chamado pelo useEffect de currentPage
        } else {
          // Se já estamos na página correta, buscar diretamente
          fetchPage(currentPage);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalPages]);

    // Carregar página quando currentPage mudar (navegação entre páginas)
    useEffect(() => {
      if (token && authToken && totalPages !== null && currentPage) {
        fetchPage(currentPage);

        // Resetar scroll para o topo
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    // Função auxiliar para verificar e executar mudança de página
    const requestPageChange = async (newPage) => {
      // Se não tem callback de validação, permitir mudança diretamente
      if (!onRequestPageChange) {
        setCurrentPage(newPage);
        return;
      }

      // Se tem callback, solicitar permissão ao componente pai
      try {
        const allowed = await onRequestPageChange(newPage);
        if (allowed) {
          setCurrentPage(newPage);
        }
        // Se não foi permitido, não fazer nada (o componente pai já tratou)
      } catch (error) {
        console.error("Erro ao solicitar mudança de página:", error);
        // Em caso de erro, permitir a navegação
        setCurrentPage(newPage);
      }
    };

    // Handlers de navegação
    const handlePrevPage = () => {
      if (currentPage > 1) {
        requestPageChange(currentPage - 1);
      }
    };

    const handleNextPage = () => {
      if (currentPage < totalPages) {
        requestPageChange(currentPage + 1);
      }
    };

    const handleFirstPage = () => {
      requestPageChange(1);
    };

    const handleLastPage = () => {
      if (totalPages) {
        requestPageChange(totalPages);
      }
    };

    // Handlers de zoom
    const handleZoomIn = () => {
      setScale((prev) => Math.min(prev + 0.2, 3.0));
    };

    const handleZoomOut = () => {
      setScale((prev) => Math.max(prev - 0.2, 0.3));
    };

    const handleBestFit = () => {
      if (containerDimensions.width > 0 && pageWidth > 0) {
        const padding = 40;
        const availableWidth = containerDimensions.width - padding;

        // Ajustar apenas pela largura - usuário faz scroll vertical
        const bestFitScale = Math.min(availableWidth / pageWidth, 3.0);
        setScale(bestFitScale);
      } else {
        setScale(1.0);
      }
    };

    // Handler de download - implementação interna usando o token
    const handleDownload = async () => {
      if (!token || !authToken) {
        console.error("Token não disponível para download");
        return;
      }

      try {
        const downloadUrl = `${API_BASE_URL}/files/download?token=${encodeURIComponent(token)}&Servicename=${encodeURIComponent(serviceName)}`;

        const response = await fetch(downloadUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
            accept: "application/octet-stream",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Erro ao fazer download: ${response.status} - ${response.statusText}`,
          );
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName || "documento.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Erro ao fazer download do PDF:", error);
        setError(`Falha ao fazer download: ${error.message}`);
      }
    };

    // Handler para salvar alterações do selection box
    const handleSaveSelectionBox = async () => {
      if (!selectionBox || !hasChanges) {
        return;
      }

      // Verificar se temos todas as informações necessárias para a API
      if (!jobId || !documentId || !itemId || !documentoOrigemId) {
        console.error("❌ Informações insuficientes para salvar:", {
          jobId,
          documentId,
          itemId,
          documentoOrigemId,
        });
        setError(
          "Informações insuficientes para salvar a referência de imagem",
        );
        return;
      }

      try {
        setSaving(true);
        setError(null);

        let apiResult = null;

        // Se há callback, usar o callback (comportamento antigo)
        if (onSaveSelectionBox) {
          await onSaveSelectionBox(selectionBox);
        } else {
          // Caso contrário, fazer a chamada de API diretamente
          console.log("💾 Salvando referência de imagem via API:", {
            jobId,
            documentId,
            itemId,
            documentoOrigemId,
            pagina: selectionBox.page || currentPage,
            coordenadas: {
              x1: selectionBox.x1,
              y1: selectionBox.y1,
              x2: selectionBox.x2,
              y2: selectionBox.y2,
            },
          });

          const apiUrl = `${API_BASE_URL}/doc_creator/review/update-image-reference/${jobId}`;

          const requestBody = {
            documento_id: documentId,
            item_id: itemId,
            documento_origem_id: documentoOrigemId,
            pagina: selectionBox.page || currentPage,
            coordenadas: {
              x1: selectionBox.x1,
              y1: selectionBox.y1,
              x2: selectionBox.x2,
              y2: selectionBox.y2,
            },
          };

          const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
              accept: "application/json",
            },
            body: JSON.stringify(requestBody),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              errorData.detail ||
                `Erro ao salvar: ${response.status} - ${response.statusText}`,
            );
          }

          apiResult = await response.json();
          console.log("✅ Referência de imagem salva com sucesso:", apiResult);
        }

        // Atualizar o estado original após salvar com sucesso
        setOriginalSelectionBox({ ...selectionBox });
        setHasChanges(false);

        // Notificar o componente pai sobre a atualização bem-sucedida
        if (onImageReferenceUpdated && apiResult) {
          console.log(
            "🔔 Notificando componente pai sobre atualização da referência de imagem",
          );
          onImageReferenceUpdated(apiResult);
        }
      } catch (error) {
        console.error("❌ Erro ao salvar selection box:", error);
        setError(`Falha ao salvar: ${error.message}`);
      } finally {
        setSaving(false);
      }
    };

    // Handler para criar um retângulo padrão (90% da largura e 90% da altura, centralizado)
    const handleCreateDefaultSelectionBox = () => {
      if (!pageWidth || !pageHeight) {
        console.warn("⚠️ Dimensões da página não disponíveis ainda");
        return;
      }

      // Calcular dimensões: 90% da largura e altura
      const width = pageWidth * 0.9;
      const height = pageHeight * 0.9;

      // Calcular posição: centralizado
      const x1 = (pageWidth - width) / 2;
      const y1 = (pageHeight - height) / 2;
      const x2 = x1 + width;
      const y2 = y1 + height;

      const newBox = {
        x1,
        y1,
        x2,
        y2,
        page: currentPage,
      };

      console.log("🎨 Criando retângulo padrão:", {
        newBox,
        pageWidth,
        pageHeight,
        percentages: {
          width: "90%",
          height: "90%",
          position: "centralizado",
        },
      });

      // Definir o retângulo
      setSelectionBox(newBox);

      // NÃO definir originalSelectionBox - deixar undefined para que hasChanges seja true
      // Isso faz com que o botão de salvar apareça imediatamente
      setOriginalSelectionBox(null);

      // hasChanges será automaticamente true porque originalSelectionBox é null
    };

    // Calcular escala automática para "best fit"
    const calculateBestFitScale = () => {
      if (containerDimensions.width > 0 && pageWidth > 0) {
        const padding = 40;
        const availableWidth = containerDimensions.width - padding;

        // Ajustar apenas pela largura - usuário faz scroll vertical
        return Math.min(availableWidth / pageWidth, 3.0);
      }
      return 1.0;
    };

    // Handlers para o retângulo de seleção
    const handleMouseDownOnBox = (e) => {
      // Não permitir interação se não for editável
      if (!editavel || !selectionBox) return;

      e.preventDefault();
      e.stopPropagation();

      // Salvar posição do mouse em coordenadas da tela
      isDraggingRef.current = true;
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        boxX1: selectionBox.x1,
        boxY1: selectionBox.y1,
      };
    };

    const handleMouseDownOnHandle = (handle, e) => {
      // Não permitir interação se não for editável
      if (!editavel || !selectionBox) return;

      e.preventDefault();
      e.stopPropagation();

      isResizingRef.current = true;
      resizeHandleRef.current = handle;
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        box: { ...selectionBox },
      };
    };

    const handleMouseMove = useCallback((e) => {
      if (!isDraggingRef.current && !isResizingRef.current) return;

      const currentBox = selectionBoxStateRef.current;
      if (!currentBox) return;

      const currentScale = scaleRef.current;
      const maxWidth = pageWidthRef.current;
      const maxHeight = pageHeightRef.current;

      // Se as dimensões da página ainda não foram carregadas, não aplicar limites
      if (maxWidth === 0 || maxHeight === 0) return;

      if (isDraggingRef.current) {
        // DRAG: Mover o retângulo
        const deltaX = (e.clientX - dragStartRef.current.x) / currentScale;
        const deltaY = (e.clientY - dragStartRef.current.y) / currentScale;

        const width = currentBox.x2 - currentBox.x1;
        const height = currentBox.y2 - currentBox.y1;

        let newX1 = dragStartRef.current.boxX1 + deltaX;
        let newY1 = dragStartRef.current.boxY1 + deltaY;

        // Limitar movimento dentro da página
        newX1 = Math.max(0, Math.min(newX1, maxWidth - width));
        newY1 = Math.max(0, Math.min(newY1, maxHeight - height));

        setSelectionBox({
          ...currentBox,
          x1: newX1,
          y1: newY1,
          x2: newX1 + width,
          y2: newY1 + height,
        });
      } else if (isResizingRef.current) {
        // RESIZE: Redimensionar o retângulo
        const deltaX = (e.clientX - dragStartRef.current.x) / currentScale;
        const deltaY = (e.clientY - dragStartRef.current.y) / currentScale;

        const originalBox = dragStartRef.current.box;
        let newBox = { ...currentBox };

        switch (resizeHandleRef.current) {
          case "tl": // top-left
            newBox.x1 = originalBox.x1 + deltaX;
            newBox.y1 = originalBox.y1 + deltaY;
            break;
          case "tr": // top-right
            newBox.x2 = originalBox.x2 + deltaX;
            newBox.y1 = originalBox.y1 + deltaY;
            break;
          case "bl": // bottom-left
            newBox.x1 = originalBox.x1 + deltaX;
            newBox.y2 = originalBox.y2 + deltaY;
            break;
          case "br": // bottom-right
            newBox.x2 = originalBox.x2 + deltaX;
            newBox.y2 = originalBox.y2 + deltaY;
            break;
          case "t": // top
            newBox.y1 = originalBox.y1 + deltaY;
            break;
          case "b": // bottom
            newBox.y2 = originalBox.y2 + deltaY;
            break;
          case "l": // left
            newBox.x1 = originalBox.x1 + deltaX;
            break;
          case "r": // right
            newBox.x2 = originalBox.x2 + deltaX;
            break;
          default:
            break;
        }

        // Garantir que x1 < x2 e y1 < y2
        if (newBox.x1 > newBox.x2)
          [newBox.x1, newBox.x2] = [newBox.x2, newBox.x1];
        if (newBox.y1 > newBox.y2)
          [newBox.y1, newBox.y2] = [newBox.y2, newBox.y1];

        // Limitar coordenadas dentro dos limites da página
        newBox.x1 = Math.max(0, Math.min(newBox.x1, maxWidth));
        newBox.y1 = Math.max(0, Math.min(newBox.y1, maxHeight));
        newBox.x2 = Math.max(0, Math.min(newBox.x2, maxWidth));
        newBox.y2 = Math.max(0, Math.min(newBox.y2, maxHeight));

        // Garantir dimensões mínimas (opcional, mas recomendado)
        const minSize = 10; // pixels mínimos
        if (newBox.x2 - newBox.x1 < minSize) {
          if (
            resizeHandleRef.current.includes("l") ||
            resizeHandleRef.current === "l"
          ) {
            // left handle
            newBox.x1 = newBox.x2 - minSize;
          } else {
            // right handle
            newBox.x2 = newBox.x1 + minSize;
          }
        }
        if (newBox.y2 - newBox.y1 < minSize) {
          if (
            resizeHandleRef.current.includes("t") ||
            resizeHandleRef.current === "t"
          ) {
            // top handle
            newBox.y1 = newBox.y2 - minSize;
          } else {
            // bottom handle
            newBox.y2 = newBox.y1 + minSize;
          }
        }

        setSelectionBox(newBox);
      }
    }, []);

    const handleMouseUp = useCallback(() => {
      // Não usar setState - apenas refs para não causar re-render
      isDraggingRef.current = false;
      isResizingRef.current = false;
      resizeHandleRef.current = null;
    }, []);

    // Adicionar event listeners para mouse move e mouse up
    // Mantém os listeners sempre ativos (não depende de selectionBox para evitar remoção durante drag)
    useEffect(() => {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }, [handleMouseMove, handleMouseUp]);

    // Aplicar best fit inicialmente quando carregar a primeira página
    useEffect(() => {
      if (pageBase64 && pageWidth > 0) {
        const bestFitScale = calculateBestFitScale();
        if (bestFitScale !== scale) {
          setScale(bestFitScale);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageBase64, pageWidth, containerDimensions.width]); // calculateBestFitScale e scale não devem ser dependências para evitar loop

    // Validações
    if (!token) {
      return (
        <div style={{ width, height }} className="pdf-empty-state">
          <div className="pdf-empty-state-error">
            <AlertCircle
              size={48}
              style={{ margin: "0 auto 8px", display: "block" }}
            />
            <p className="font-semibold">Token não fornecido</p>
          </div>
        </div>
      );
    }

    // Preparar dados do PDF
    const pdfData = pageBase64
      ? `data:application/pdf;base64,${pageBase64}`
      : null;

    return (
      <div style={{ width, height }} className="pdf-container">
        {/* Barra de Ferramentas */}
        {showToolbar && (
          <div className="pdf-toolbar-section">
            <div className="pdf-toolbar">
              {/* Controles principais - lado esquerdo */}
              <div className="flex-row gap-4">
                {/* Navegação */}
                <button
                  onClick={handleFirstPage}
                  disabled={currentPage <= 1 || loading || loadingPageCount}
                  className="pdf-nav-btn"
                  title="Primeira página"
                >
                  <SkipBack size={16} />
                </button>

                <button
                  onClick={handlePrevPage}
                  disabled={currentPage <= 1 || loading || loadingPageCount}
                  className="pdf-nav-btn"
                  title="Página anterior"
                >
                  <ChevronLeft size={16} />
                </button>

                {/* Input de página */}
                <div className="pdf-page-input-group">
                  <input
                    type="number"
                    value={pageInput ?? currentPage}
                    onChange={(e) => {
                      // Apenas armazena o valor digitado — navegação só ocorre em
                      // Enter ou blur, para o usuário poder terminar de digitar
                      // números de múltiplos dígitos sem perder o foco a cada tecla.
                      setPageInput(e.target.value);
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        const page = parseInt(pageInput);
                        if (page && page >= 1 && page <= totalPages) {
                          requestPageChange(page);
                        }
                        setPageInput(null);
                      }
                    }}
                    onBlur={() => {
                      const page = parseInt(pageInput);
                      if (page && page >= 1 && page <= totalPages) {
                        requestPageChange(page);
                      }
                      setPageInput(null);
                    }}
                    disabled={
                      loading || loadingPageCount || totalPages === null
                    }
                    className="pdf-page-input"
                    min="1"
                    max={totalPages || 1}
                  />
                  <span className="pdf-page-total">
                    /{" "}
                    {loadingPageCount
                      ? "..."
                      : totalPages !== null
                        ? totalPages
                        : "..."}
                  </span>
                </div>

                <button
                  onClick={handleNextPage}
                  disabled={
                    currentPage >= (totalPages || 1) ||
                    loading ||
                    loadingPageCount
                  }
                  className="pdf-nav-btn"
                  title="Próxima página"
                >
                  <ChevronRight size={16} />
                </button>

                <button
                  onClick={handleLastPage}
                  disabled={
                    currentPage >= (totalPages || 1) ||
                    loading ||
                    loadingPageCount
                  }
                  className="pdf-nav-btn"
                  title="Última página"
                >
                  <SkipForward size={16} />
                </button>

                {/* Separador */}
                <div className="pdf-toolbar-separator"></div>

                {/* Zoom */}
                <button
                  onClick={handleZoomOut}
                  disabled={loading || loadingPageCount}
                  className="pdf-nav-btn"
                  title="Diminuir zoom"
                >
                  <ZoomOut size={16} />
                </button>

                <span className="pdf-zoom-label">
                  {Math.round(scale * 100)}%
                </span>

                <button
                  onClick={handleZoomIn}
                  disabled={loading || loadingPageCount}
                  className="pdf-nav-btn"
                  title="Aumentar zoom"
                >
                  <ZoomIn size={16} />
                </button>

                <button
                  onClick={handleBestFit}
                  disabled={loading || loadingPageCount}
                  className="pdf-nav-btn"
                  title="Ajustar largura"
                >
                  <Maximize2 size={16} />
                </button>
              </div>

              {/* Controles secundários - lado direito */}
              <div className="flex-row gap-4">
                {/* Botão Criar Retângulo - só aparece quando é item de imagem, não há retângulo e é editável */}
                {currentItemIsImage &&
                  !selectionBox &&
                  editavel &&
                  (onSaveSelectionBox ||
                    (jobId && documentId && itemId && documentoOrigemId)) && (
                    <button
                      onClick={handleCreateDefaultSelectionBox}
                      disabled={
                        loading || loadingPageCount || !pageWidth || !pageHeight
                      }
                      className="pdf-create-area-btn"
                      title="Criar retângulo de área customizável"
                    >
                      <div className="pdf-create-area-icon" />
                      Criar Área
                    </button>
                  )}

                {/* Botão Salvar - só aparece quando há mudanças e é editável */}
                {hasChanges &&
                  editavel &&
                  (onSaveSelectionBox ||
                    (jobId && documentId && itemId && documentoOrigemId)) && (
                    <button
                      onClick={handleSaveSelectionBox}
                      disabled={saving || loading || loadingPageCount}
                      className="pdf-save-btn"
                      style={{
                        backgroundColor: saving
                          ? "var(--ikaro-gray-medium)"
                          : "var(--ikaro-status-success)",
                      }}
                      title={
                        saving
                          ? "Salvando alterações..."
                          : "Salvar alterações do retângulo"
                      }
                    >
                      <Save size={16} />
                    </button>
                  )}

                <button
                  onClick={handleDownload}
                  disabled={loading || loadingPageCount}
                  className="pdf-download-btn"
                  title="Download do arquivo completo"
                >
                  <Download size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Área de visualização do PDF */}
        <div
          ref={containerRef}
          className="pdf-viewer-area items-start"
          style={{ height: showToolbar ? "calc(100% - 44px)" : "100%" }}
        >
          {/* Loading inicial (page count) */}
          {loadingPageCount && (
            <div className="pdf-loading-state">
              <CircularProgress
                style={{ color: "var(--ikaro-orange-primary)" }}
              />
              <Typography
                variant="body2"
                style={{ color: "var(--ikaro-text-primary)" }}
              >
                Carregando informações do documento...
              </Typography>
            </div>
          )}

          {/* Loading da página */}
          {!loadingPageCount && loading && (
            <div className="pdf-loading-state pdf-loading-state-offset">
              <CircularProgress
                style={{ color: "var(--ikaro-orange-primary)" }}
              />
              <Typography
                variant="body2"
                style={{ color: "var(--ikaro-text-primary)" }}
              >
                Carregando página {currentPage}...
              </Typography>
            </div>
          )}

          {/* Erro */}
          {error && !loading && !loadingPageCount && (
            <div className="pdf-error-state">
              <AlertCircle size={48} color="var(--ikaro-status-error)" />
              <Typography
                variant="body1"
                style={{ color: "var(--ikaro-text-primary)", fontWeight: 600 }}
              >
                Erro ao carregar PDF
              </Typography>
              <Typography
                variant="body2"
                style={{ color: "var(--ikaro-text-secondary)" }}
              >
                {error}
              </Typography>
            </div>
          )}

          {/* PDF renderizado com react-pdf */}
          {!loadingPageCount && !loading && !error && pdfData && (
            <div className="pdf-content-wrapper">
              <Document
                file={pdfData}
                loading={
                  <div className="pdf-doc-loading">
                    <CircularProgress
                      style={{ color: "var(--ikaro-orange-primary)" }}
                    />
                  </div>
                }
                error={
                  <div className="pdf-doc-error">
                    <AlertCircle size={32} style={{ marginRight: "8px" }} />
                    <span>Erro ao renderizar PDF</span>
                  </div>
                }
              >
                <div
                  className={`relative pdf-page-container ${selectionBox ? "has-selection-box" : ""}`}
                >
                  <Page
                    pageNumber={1}
                    scale={scale}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    className="shadow-lg pdf-page"
                    onLoadSuccess={(page) => {
                      setPageWidth(page.originalWidth);
                      setPageHeight(page.originalHeight);
                    }}
                    loading={
                      <div className="pdf-page-loading">
                        <CircularProgress
                          style={{ color: "var(--ikaro-orange-primary)" }}
                        />
                      </div>
                    }
                  />

                  {/* Retângulo de seleção */}
                  {selectionBox &&
                    (selectionBox.page === currentPage ||
                      !selectionBox.page) && (
                      <div
                        ref={selectionBoxRef}
                        onMouseDown={handleMouseDownOnBox}
                        className="pdf-selection-box"
                        style={{
                          left: `${selectionBox.x1 * scale}px`,
                          top: `${selectionBox.y1 * scale}px`,
                          width: `${(selectionBox.x2 - selectionBox.x1) * scale}px`,
                          height: `${(selectionBox.y2 - selectionBox.y1) * scale}px`,
                          cursor: editavel ? "move" : "default",
                        }}
                      >
                        {/* Handles de redimensionamento - só aparecem quando editável */}
                        {editavel && (
                          <>
                            <div
                              onMouseDown={(e) =>
                                handleMouseDownOnHandle("tl", e)
                              }
                              className="pdf-resize-handle pdf-resize-handle-tl"
                            />
                            <div
                              onMouseDown={(e) =>
                                handleMouseDownOnHandle("tr", e)
                              }
                              className="pdf-resize-handle pdf-resize-handle-tr"
                            />
                            <div
                              onMouseDown={(e) =>
                                handleMouseDownOnHandle("bl", e)
                              }
                              className="pdf-resize-handle pdf-resize-handle-bl"
                            />
                            <div
                              onMouseDown={(e) =>
                                handleMouseDownOnHandle("br", e)
                              }
                              className="pdf-resize-handle pdf-resize-handle-br"
                            />
                            <div
                              onMouseDown={(e) =>
                                handleMouseDownOnHandle("t", e)
                              }
                              className="pdf-resize-handle pdf-resize-handle-t"
                            />
                            <div
                              onMouseDown={(e) =>
                                handleMouseDownOnHandle("b", e)
                              }
                              className="pdf-resize-handle pdf-resize-handle-b"
                            />
                            <div
                              onMouseDown={(e) =>
                                handleMouseDownOnHandle("l", e)
                              }
                              className="pdf-resize-handle pdf-resize-handle-l"
                            />
                            <div
                              onMouseDown={(e) =>
                                handleMouseDownOnHandle("r", e)
                              }
                              className="pdf-resize-handle pdf-resize-handle-r"
                            />
                          </>
                        )}
                      </div>
                    )}
                </div>
              </Document>
            </div>
          )}
        </div>
      </div>
    );
  },
);

OptimizedPDFViewer.displayName = "OptimizedPDFViewer";

export default OptimizedPDFViewer;
